export const getDateRange = (viewMode, offset = 0) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  switch(viewMode) {
    case 'week':
      // 3-4 days past, today, 3-4 days future (centered on today)
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - 3 + (offset * 7));
      const weekEnd = new Date(today);
      weekEnd.setDate(today.getDate() + 4 + (offset * 7));
      return { start: weekStart, end: weekEnd };
      
    case 'month':
      // 1 week future + 3 weeks past (28 days total)
      const monthStart = new Date(today);
      monthStart.setDate(today.getDate() - 21 + (offset * 30));
      const monthEnd = new Date(today);
      monthEnd.setDate(today.getDate() + 7 + (offset * 30));
      return { start: monthStart, end: monthEnd };
      
    case 'quarter':
      // 1 month future + 2 months past (90 days total)
      const quarterStart = new Date(today);
      quarterStart.setMonth(today.getMonth() - 2 + offset);
      quarterStart.setDate(1);
      const quarterEnd = new Date(today);
      quarterEnd.setMonth(today.getMonth() + 1 + offset);
      quarterEnd.setDate(0); // Last day of next month
      return { start: quarterStart, end: quarterEnd };
      
    case 'year':
      // 2 months future + 9 months past (330 days total)
      const yearStart = new Date(today);
      yearStart.setMonth(today.getMonth() - 9 + offset);
      yearStart.setDate(1);
      const yearEnd = new Date(today);
      yearEnd.setMonth(today.getMonth() + 2 + offset);
      yearEnd.setDate(0); // Last day of 2 months ahead
      return { start: yearStart, end: yearEnd };
      
    default:
      return { start: today, end: today };
  }
};

export const createCell = (dateObj, entries, rangeStart, rangeEnd) => {
  const isoDate = dateObj.toISOString().slice(0, 10);
  const cellDate = new Date(dateObj);
  cellDate.setHours(0, 0, 0, 0);
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const isToday = cellDate.getTime() === today.getTime();
  const isFuture = cellDate > today;
  
  return {
    isoDate,
    dateObj: new Date(dateObj),
    count: entries[isoDate]?.length || 0,
    isMuted: dateObj < rangeStart || dateObj > rangeEnd,
    isToday,
    isFuture
  };
};

export const seededRandom = (seed) => {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
};

export const generateMockEntries = () => {
  const today = new Date();
  const data = {};

  for (let i = 0; i < 365; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() - i);
    const iso = date.toISOString().slice(0, 10);
    const rand = seededRandom(date.getTime());

    let count = 0;
    if (rand > 0.85) count = 3;
    else if (rand > 0.65) count = 2;
    else if (rand > 0.45) count = 1;

    if (count > 0) {
      data[iso] = Array.from({ length: count }).map((_, idx) => ({
        id: `${iso}-${idx}`,
        text: `Pocket ${idx + 1}`,
        done: rand > 0.7 ? idx % 2 === 0 : false
      }));
    }
  }

  return data;
};

// Time helper functions
export const parseTime = (timeStr) => {
  if (!timeStr) return null;
  const [hour, minute] = timeStr.split(':').map(Number);
  return { hour, minute };
};

export const isAllDayEntry = (entry) => {
  return !entry.startTime && !entry.endTime;
};

export const getTimeDisplay = (entry) => {
  if (isAllDayEntry(entry)) {
    return 'All day';
  }
  if (entry.startTime) {
    return entry.startTime;
  }
  return '';
};

export const getTimeRangeTooltip = (entry) => {
  if (isAllDayEntry(entry)) {
    return 'All day';
  }
  if (entry.startTime && entry.endTime) {
    return `${entry.startTime} - ${entry.endTime}`;
  }
  if (entry.startTime) {
    return entry.startTime;
  }
  return '';
};

export const getTimeSlotRange = (entry) => {
  // If no time, return empty array (will be handled separately)
  if (isAllDayEntry(entry)) {
    return [];
  }
  
  // If only startTime, return just that hour
  if (entry.startTime && !entry.endTime) {
    const startTime = parseTime(entry.startTime);
    if (startTime) {
      return [startTime.hour];
    }
    return [];
  }
  
  // If both startTime and endTime, calculate range
  if (entry.startTime && entry.endTime) {
    const startTime = parseTime(entry.startTime);
    const endTime = parseTime(entry.endTime);
    
    if (!startTime || !endTime) return [];
    
    const hours = [];
    let currentHour = startTime.hour;
    const endHour = endTime.hour;
    
    // Handle same hour (e.g., 09:00 to 09:30)
    if (currentHour === endHour) {
      return [currentHour];
    }
    
    // Handle normal range (e.g., 09:00 to 11:00)
    if (endHour > currentHour) {
      while (currentHour <= endHour) {
        hours.push(currentHour);
        currentHour++;
      }
    } else {
      // Handle span across midnight (e.g., 23:00 to 01:00)
      // For now, just show from start to 23, then 0 to end
      while (currentHour <= 23) {
        hours.push(currentHour);
        currentHour++;
      }
      currentHour = 0;
      while (currentHour <= endHour) {
        hours.push(currentHour);
        currentHour++;
      }
    }
    
    return hours;
  }
  
  return [];
};

